import type { ColumnSpec } from './shared';

/**
 * The CSV contract for both screens.
 *
 * One definition drives three things that must agree or the round trip breaks:
 * the template's headers, the export's headers, and what an upload will accept. The
 * FIRST accepted name is what the template and export emit; the rest are spellings a
 * hand-made or round-tripped file is likely to carry.
 */

export const APPLICATION_COLUMNS: readonly ColumnSpec[] = [
  {
    field: 'application_id_code',
    accepts: ['Application ID', 'Application ID/Code', 'Application Code', 'App ID', 'ID'],
  },
  { field: 'name', accepts: ['Application Name', 'Name', 'App Name'] },
  {
    field: 'it_hardware_infrastructure',
    accepts: ['IT Hardware Infrastructure', 'IT Hardware / Infrastructure', 'Infrastructure', 'Hardware'],
  },
  { field: 'date_of_introduction', accepts: ['Date of Introduction', 'Introduced', 'Go Live'] },
  { field: 'contract_end_date', accepts: ['Contract End Date', 'Contract End', 'Renewal Date'] },
  { field: 'application_owner', accepts: ['Application Owner', 'Owner', 'App Owner'] },
  { field: 'app_owner_email', accepts: ['App Owner Email', 'Application Owner Email', 'Owner Email'] },
  {
    field: 'technical_owner_custodian',
    accepts: ['Technical Owner', 'Technical Owner / Custodian', 'Custodian'],
  },
  { field: 'to_email', accepts: ['Technical Owner Email', 'TO Email'] },
  /*
   * The three ownership pairs added in migration 0082, owner-directed 2026-08-25.
   *
   * They are in the CONTRACT now, not just on the panel. Sending a questionnaire refuses
   * unless every owner the framework asks for is present on every application, and typing
   * three addresses into 579 detail panels is not a way to populate them. A field that can
   * be edited has to be maintainable in bulk as well as one at a time.
   *
   * 22 headers become 28. Files carrying the old 22 still import unchanged: the importer
   * matches on header NAME, so a column that is absent is simply not set — it is not read
   * positionally and a short row is not an error.
   */
  {
    field: 'business_process_owner',
    accepts: ['Business Process Owner', 'BPO', 'Process Owner'],
  },
  { field: 'bpo_email', accepts: ['BPO Email', 'Business Process Owner Email'] },
  { field: 'security_owner', accepts: ['Security Owner'] },
  { field: 'security_owner_email', accepts: ['Security Owner Email'] },
  { field: 'executive_owner', accepts: ['Executive Owner', 'Sponsor'] },
  { field: 'executive_owner_email', accepts: ['Executive Owner Email', 'Sponsor Email'] },
  {
    field: 'business_function_domain',
    accepts: ['Business Function/Domain', 'Business Function / Domain', 'Business Function', 'Domain'],
  },
  { field: 'application_type', accepts: ['Application Type', 'Type', 'App Type'] },
  { field: 'technology_stack', accepts: ['Technology Stack', 'Technology', 'Tech Stack'] },
  { field: 'user_count', accepts: ['User Count', 'Users'] },
  { field: 'business_criticality', accepts: ['Business Criticality', 'Criticality'] },
  {
    field: 'technical_health_status',
    accepts: ['Technical Health Status', 'Health Status', 'Technical Health'],
  },
  {
    field: 'integration_dependencies',
    accepts: ['Integration Dependencies', 'Dependencies', 'Integrations'],
  },
  { field: 'vendor_supplier', accepts: ['Vendor/Supplier', 'Vendor / Supplier', 'Vendor', 'Supplier'] },
  {
    field: 'parent_application_id',
    accepts: ['Parent Application ID', 'Parent Application', 'Parent ID', 'Parent'],
  },
  {
    field: 'description',
    accepts: ['Application Description', 'Description', 'Notes'],
  },
  { field: 'internal_number', accepts: ['Internal Number', 'Internal No', 'Internal #'] },
  {
    field: 'l1_business_functional_area',
    accepts: ['L1 - Business Functional Area', 'L1 Business Functional Area', 'L1'],
  },
  {
    field: 'l2_capability_function',
    accepts: ['L2 - Capability Function', 'L2 Capability Function', 'L2'],
  },
];

export const RUN_RATE_COLUMNS: readonly ColumnSpec[] = [
  {
    field: 'application_id_code',
    accepts: ['Application ID', 'Application ID/Code', 'Application Code', 'App ID', 'ID'],
  },
  { field: 'application_name', accepts: ['Application Name', 'Name', 'App Name'] },
  {
    field: 'total_annual_cost',
    accepts: [
      'Total Annual Cost to Operate and Support',
      'Total Annual Cost',
      'Total Cost',
      'Total',
    ],
  },
  {
    field: 'annual_software_licensing_fees',
    accepts: ['Annual Software Licensing Fees', 'Software Licensing', 'Licensing'],
  },
  {
    field: 'annual_third_party_vendor_support',
    accepts: [
      'Annual 3rd Party Vendor Support Cost',
      '3rd Party Vendor',
      'Vendor Support',
      'Third Party Vendor Support',
    ],
  },
  {
    field: 'annual_infrastructure_cost',
    accepts: ['Annual Infrastructure Cost', 'Infrastructure'],
  },
  {
    field: 'indirect_operational_costs',
    accepts: ['Indirect Operational Costs', 'Indirect Ops', 'Operational Costs'],
  },
  {
    field: 'cloud_saas_subscription_cost_impact',
    accepts: ['Cloud/SaaS Subscription Costs', 'Cloud/SaaS', 'Cloud / SaaS', 'Cloud SaaS'],
  },
];

/** The column every update file must carry — it is how a row finds its record. */
export const MATCH_COLUMN = APPLICATION_COLUMNS[0]!;

/** Sample rows, so the template shows the shape rather than only naming it. */
export const APPLICATION_SAMPLES: readonly Record<string, string>[] = [
  {
    application_id_code: 'APP-9001',
    name: 'Example Finance System',
    it_hardware_infrastructure: 'On-premise',
    application_owner: 'Jane Doe',
    app_owner_email: 'jane.doe@example.com',
    business_function_domain: 'Finance',
    application_type: 'SaaS Application',
    technology_stack: 'Java / Oracle',
    user_count: '250',
    business_criticality: 'Business Critical',
    technical_health_status: 'Healthy',
    vendor_supplier: 'Example Corp',
    contract_end_date: '2027-06-30',
  },
  {
    application_id_code: 'APP-9002',
    name: 'Example HR Portal',
    application_type: 'Website / Portal',
    business_function_domain: 'Human Resources',
    user_count: '1200',
    business_criticality: 'Important',
  },
];

export const RUN_RATE_SAMPLES: readonly Record<string, string>[] = [
  {
    application_id_code: 'APP-9001',
    application_name: 'Example Finance System',
    total_annual_cost: '250000',
    annual_software_licensing_fees: '120000',
    annual_third_party_vendor_support: '40000',
    annual_infrastructure_cost: '50000',
    indirect_operational_costs: '30000',
    cloud_saas_subscription_cost_impact: '10000',
  },
  {
    application_id_code: 'APP-9002',
    application_name: 'Example HR Portal',
    total_annual_cost: '80000',
    annual_software_licensing_fees: '60000',
    annual_third_party_vendor_support: '0',
    annual_infrastructure_cost: '15000',
    indirect_operational_costs: '5000',
    cloud_saas_subscription_cost_impact: '0',
  },
];
